import { z } from 'zod'

const MapPoiIconSchema = z.object({
  id: z.number(),
  name: z.string().nullable().catch(null),
  url: z.string(),
  alternativeText: z.string().nullable().catch(null),
})

/** Strapi media may be a single object or a one-item array. */
function mediaOrFirst(val: unknown): unknown {
  if (val == null) return val
  return Array.isArray(val) ? (val[0] ?? null) : val
}

const MapPoiMediaSchema = z.preprocess(
  mediaOrFirst,
  MapPoiIconSchema.nullable().catch(null),
)

const MapPoiTransportModeSchema = z.object({
  id: z.number(),
  text: z.string().nullable(),
  icon: MapPoiIconSchema.nullable().catch(null),
})

const MapPoiDistanceDetailsSchema = z.object({
  id: z.number().optional().catch(undefined),
  text: z.string().nullable(),
  icon: MapPoiTransportModeSchema.nullable().catch(null),
})

/** CMS may send distanceDetails as a plain string or as { text, icon }. */
function normalizeDistanceDetails(val: unknown, fallbackId = 0): unknown {
  if (val == null) return null
  if (typeof val === 'string') {
    const text = val.trim()
    if (!text) return null
    return { id: fallbackId, text, icon: null }
  }
  if (typeof val === 'object' && !Array.isArray(val)) {
    const record = val as Record<string, unknown>
    // Some payloads nest the copy under `distance` instead of `text`.
    if (record.text == null && typeof record.distance === 'string') {
      return { ...record, text: record.distance }
    }
  }
  return val
}

const MapPoiCategorySchema = z.object({
  id: z.number(),
  documentId: z.string().optional().catch(undefined),
  name: z.string().nullable().catch(null),
  icon: MapPoiMediaSchema,
  poiMarker: MapPoiMediaSchema.optional(),
})

function toCoordNumber(val: unknown): number | null {
  if (val == null || val === '') return null
  const n = typeof val === 'number' ? val : Number(val)
  return Number.isFinite(n) ? n : null
}

const MapPoiCoordinatesSchema = z.preprocess((val) => {
  if (val == null) return null
  const obj = Array.isArray(val) ? val[0] : val
  if (!obj || typeof obj !== 'object') return null
  const record = obj as Record<string, unknown>
  return {
    id: record.id,
    latitude: toCoordNumber(record.latitude ?? record.lat),
    longitude: toCoordNumber(record.longitude ?? record.lng),
  }
}, z.object({
  id: z.number().optional().catch(undefined),
  latitude: z.number().nullable().optional().catch(null),
  longitude: z.number().nullable().optional().catch(null),
}).nullable().catch(null))

const MapPoiFieldsSchema = z.object({
  id: z.number(),
  documentId: z.string().optional().catch(undefined),
  title: z.string().nullable().catch(null),
  description: z.string().nullable().catch(null),
  distanceDetails: z
    .preprocess(
      (val) => normalizeDistanceDetails(val),
      MapPoiDistanceDetailsSchema.nullable().catch(null),
    )
    .optional()
    .catch(null),
  category: MapPoiCategorySchema.nullable().catch(null),
  address: z.string().nullable().catch(null),
  phone: z.string().nullable().catch(null),
  coordinates: MapPoiCoordinatesSchema,
  websiteUrl: z.string().nullable().catch(null),
  mapUrl: z.string().nullable().catch(null),
  order: z.number().nullable().optional().catch(null),
  hidePoi: z.boolean().nullable().optional().catch(null),
  highlightPoi: z.boolean().nullable().optional().catch(null),
})

/** Accept `poiCategory` as an alias for the `category` relation. */
export const MapPoiSchema = z.preprocess((val) => {
  if (!val || typeof val !== 'object') return val
  const record = { ...(val as Record<string, unknown>) }
  if (record.category == null && record.poiCategory != null) {
    record.category = record.poiCategory
    delete record.poiCategory
  }

  const id = typeof record.id === 'number' ? record.id : 0
  record.distanceDetails = normalizeDistanceDetails(
    record.distanceDetails ?? record.distance,
    id,
  )
  return record
}, MapPoiFieldsSchema)

export const MapPoisResponseSchema = z.object({
  data: z.array(MapPoiSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }),
  }),
})

/**
 * Home map “additional POIs” — inline CMS entries with icon, marker, and
 * distance already on the home page payload (not the global map-pois collection).
 */
export const AdditionalMapPoiSchema = z.object({
  id: z.number(),
  title: z.string().nullable().catch(null),
  category: z.string().nullable().catch(null),
  distance: z.string().nullable().catch(null),
  icon: MapPoiMediaSchema,
  poiMarker: MapPoiMediaSchema.optional().catch(null),
  coordinates: MapPoiCoordinatesSchema,
})

export type MapPoiType = z.infer<typeof MapPoiFieldsSchema>
export type MapPoisResponseType = z.infer<typeof MapPoisResponseSchema>
export type AdditionalMapPoiType = z.infer<typeof AdditionalMapPoiSchema>

/** Normalize home `additionalPois` into the shared MapPoi shape used by the map UI. */
export function additionalPoiToMapPoi(poi: AdditionalMapPoiType): MapPoiType {
  const categoryName = poi.category?.trim() || null

  return {
    id: poi.id,
    documentId: `additional-poi-${poi.id}`,
    title: poi.title,
    description: null,
    distanceDetails: poi.distance
      ? { id: poi.id, text: poi.distance, icon: null }
      : null,
    category: {
      id: poi.id,
      documentId: `additional-poi-category-${poi.id}`,
      name: categoryName,
      icon: poi.icon,
      poiMarker: poi.poiMarker ?? null,
    },
    address: null,
    phone: null,
    coordinates: poi.coordinates,
    websiteUrl: null,
    mapUrl: null,
    order: null,
    hidePoi: false,
    highlightPoi: false,
  }
}
